import { useEffect, useRef, useState } from 'react';
import { LuChevronDown, LuChevronUp } from 'react-icons/lu';
import ClientDeliverables from '../ClientDeliverables/ClientDeliverables';
import ProjectBenefits from '../ProjectBenefits/ProjectBenefits';
import {
  keepSectionTopDuringExpand,
  scrollSectionAfterCollapse,
} from '../../../utils/scrollAfterCollapse';

const MOBILE_QUERY = '(max-width: 767px)';
const BORDER_FLASH_DURATION = 220;

const useMobileViewport = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined'
      ? window.matchMedia(MOBILE_QUERY).matches
      : false
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const handleChange = event => setIsMobile(event.matches);

    setIsMobile(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);

    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isMobile;
};

const ProjectResultsAccordion = ({ project, projectId, enabled = false }) => {
  const [openSection, setOpenSection] = useState(null);
  const [closingSection, setClosingSection] = useState(null);
  const closingTimerRef = useRef(null);
  const itemRefs = useRef({});
  const isMobile = useMobileViewport();

  useEffect(() => {
    if (!enabled || !isMobile) {
      window.clearTimeout(closingTimerRef.current);
      setOpenSection(null);
      setClosingSection(null);
    }
  }, [enabled, isMobile]);

  useEffect(
    () => () => window.clearTimeout(closingTimerRef.current),
    []
  );

  if (!enabled) {
    return (
      <>
        <ClientDeliverables data={project.deliverables} />
        <ProjectBenefits data={project.benefits} />
      </>
    );
  }

  const sections = [
    {
      id: 'deliverables',
      title: project.deliverables?.title || 'Что получил клиент',
      hint: project.deliverables?.hint || 'Состав переданных материалов по проекту',
      content: <ClientDeliverables data={project.deliverables} />,
    },
    {
      id: 'benefits',
      title: project.benefits?.title || 'Что это дало',
      hint: project.benefits?.hint || 'Практическая польза подготовленных материалов',
      content: <ProjectBenefits data={project.benefits} />,
    },
  ];

  const closeSection = sectionId => {
    const card = itemRefs.current[sectionId];
    window.clearTimeout(closingTimerRef.current);
    scrollSectionAfterCollapse(card);
    setClosingSection(sectionId);
    setOpenSection(null);
    closingTimerRef.current = window.setTimeout(
      () => setClosingSection(null),
      BORDER_FLASH_DURATION
    );
  };

  return sections.map(({ id, title, hint, content }) => {
    const isOpen = isMobile && openSection === id;
    const isClosing = isMobile && closingSection === id;
    const panelId = `project-${projectId}-${id}-panel`;
    const headingId = `project-${projectId}-${id}-heading`;

    return (
      <div
        className={`project-intro-accordion__item project-case-card${isOpen ? ' is-open' : ''}${isClosing ? ' is-closing' : ''}`}
        key={id}
        ref={element => {
          itemRefs.current[id] = element;
        }}
      >
        <h2 className="project-intro-accordion__heading" id={headingId}>
          <button
            type="button"
            className="project-intro-accordion__toggle"
            aria-expanded={isOpen}
            aria-controls={panelId}
            onClick={() => {
              if (isOpen) {
                closeSection(id);
                return;
              }

              window.clearTimeout(closingTimerRef.current);
              setClosingSection(null);
              keepSectionTopDuringExpand(itemRefs.current[id]);
              setOpenSection(id);
            }}
          >
            <span className="project-intro-accordion__heading-copy">
              <span className="project-intro-accordion__title">{title}</span>
              <span className="project-intro-accordion__hint">
                <span>{hint}</span>
              </span>
            </span>
            {isOpen ? (
              <LuChevronUp
                className="project-intro-accordion__chevron"
                aria-hidden="true"
              />
            ) : (
              <LuChevronDown
                className="project-intro-accordion__chevron"
                aria-hidden="true"
              />
            )}
          </button>
        </h2>

        <div
          className="project-intro-accordion__panel"
          id={panelId}
          aria-labelledby={headingId}
          aria-hidden={isMobile ? !isOpen : undefined}
        >
          <div className="project-intro-accordion__panel-inner">
            {content}
            <button
              type="button"
              className="project-intro-accordion__collapse"
              aria-controls={panelId}
              onClick={() => closeSection(id)}
            >
              <span>{project.introAccordion?.collapseLabel || 'Свернуть'}</span>
              <LuChevronUp aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    );
  });
};

export default ProjectResultsAccordion;
